/**
 * NMU-444: label dictionary for the admin form modals.
 *
 * The forms are bilingual per *modal*, not per app: each one has a Thai tab and
 * an English tab, and switching to English is meant to put the whole dialog —
 * labels, placeholders, helper text, the modal title and the footer buttons —
 * into English, not just the one field being translated.
 *
 * So the "language" here is the modal's active tab, which the dialog tracks and
 * passes to `formText()`. Anything not in the dictionary falls back to the Thai
 * string it was given, so a form can be converted a piece at a time and an
 * untranslated label degrades to what it shows today rather than to a blank.
 */

/** Language keys. Tabs are keyed "1" (Thai) and "2" (English) in these dialogs. */
export const FORM_LANG = { TH: "th", EN: "en" };

/** Maps an antd Tabs activeKey to a language. Tab "2" is English everywhere. */
export const langOfTab = (activeKey) =>
  String(activeKey) === "2" ? FORM_LANG.EN : FORM_LANG.TH;

/**
 * key → { th, en }. Keys are grouped by what they label rather than by form, so
 * the same key serves every dialog that shows that field.
 */
export const FORM_TEXT = {
  // ---- tabs -------------------------------------------------------------
  tabThai: { th: "ภาษาไทย", en: "Thai" },
  tabEnglish: { th: "English", en: "English" },

  // ---- modal titles -----------------------------------------------------
  addBanner: { th: "เพิ่มแบนเนอร์", en: "Add Banner" },
  editBanner: { th: "แก้ไขแบนเนอร์", en: "Edit Banner" },
  addNews: { th: "เพิ่มข่าว", en: "Add News" },
  editNews: { th: "แก้ไขข่าว", en: "Edit News" },
  addActivity: { th: "เพิ่มกิจกรรม", en: "Add Activity" },
  editActivity: { th: "แก้ไขกิจกรรม", en: "Edit Activity" },
  addProgram: { th: "เพิ่มหลักสูตร", en: "Add Program" },
  editProgram: { th: "แก้ไขหลักสูตร", en: "Edit Program" },
  addServiceLink: { th: "เพิ่มลิงค์บริการ", en: "Add Service Link" },
  editServiceLink: { th: "แก้ไขลิงค์บริการ", en: "Edit Service Link" },
  addFooterLink: { th: "เพิ่มลิงก์ส่วนท้าย", en: "Add Footer Link" },
  editFooterLink: { th: "แก้ไขลิงก์ส่วนท้าย", en: "Edit Footer Link" },
  addHighlightBox: { th: "เพิ่มไฮไลท์", en: "Add Highlight" },
  editHighlightBox: { th: "แก้ไขไฮไลท์", en: "Edit Highlight" },

  // ---- action buttons ---------------------------------------------------
  save: { th: "บันทึก", en: "Save" },
  cancel: { th: "ยกเลิก", en: "Cancel" },

  // ---- common fields ----------------------------------------------------
  bannerName: { th: "ชื่อแบนเนอร์", en: "Banner Name" },
  bannerNameEN: { th: "ชื่อแบนเนอร์ (อังกฤษ)", en: "Banner Name (EN)" },
  bannerImage: { th: "รูปภาพ", en: "Banner Image" },
  image: { th: "รูปภาพ", en: "Image" },
  uploadFile: { th: "อัปโหลดไฟล์", en: "Upload File" },
  bannerImageHint: {
    th: "* ขนาดรูปภาพที่แนะนำคือ 1920x822 pixels.",
    en: "* Recommended image size is 1920x822 pixels.",
  },
  previewWebsite: { th: "ดูตัวอย่างเว็บไซต์", en: "Preview website" },
  link: { th: "ลิงค์", en: "Link / URL" },
  // NMU-470: a service link now points at either a typed URL or a CMS page.
  // NMU-471: per-programme admissions link behind the "Apply now" button.
  applicationUrl: { th: "ลิงค์สมัครเข้าเรียน", en: "Application Link" },
  applicationUrlHelp: {
    th: "ใส่ URL สำหรับเชื่อมต่อไปยังระบบสมัครเข้าเรียนของหลักสูตรนี้ (ไม่ใส่ก็ได้ — ถ้าเว้นว่างปุ่มจะไม่แสดงบนหน้าเว็บ)",
    en: "URL of this programme's admissions system. Optional — the website hides the button when it is empty.",
  },
  applicationUrlInvalid: {
    th: "รูปแบบ URL ไม่ถูกต้อง (ต้องขึ้นต้นด้วย https:// หรือ http://)",
    en: "Not a valid URL (must start with https:// or http://)",
  },
  linkType: { th: "ประเภท", en: "Type" },
  linkTypePlaceholder: { th: "โปรดระบุ", en: "Please select" },
  linkTypeContent: { th: "เนื้อหา", en: "Content" },
  linkTypeUrl: { th: "ลิงค์(URL)", en: "Link (URL)" },
  // NMU-562: a footer link can open the News listing filtered to one category.
  linkTypeNews: { th: "ข่าว", en: "News" },
  newsTypeField: { th: "ประเภทข่าว", en: "News Category" },
  contentField: { th: "เนื้อหา", en: "Content" },
  contentPlaceholder: { th: "พิมพ์เพื่อค้นหาเนื้อหา", en: "Type to search content" },
  keywords: { th: "คีย์เวิร์ด", en: "Keywords" },
  addKeyword: { th: "เพิ่มคีย์เวิร์ด", en: "Add Keyword" },
  openInNewTab: { th: "เปิดแท็บใหม่", en: "Open in new tab" },
  status: { th: "สถานะ", en: "Status" },
  visibility: { th: "การเผยแพร่ (Visibility)", en: "Visibility" },
  attachment: { th: "เอกสารแนบ", en: "Attachment" },
  icon: { th: "ไอคอน", en: "Icon" },
  selectIcon: { th: "เลือกไอคอน", en: "Select an icon" },

  // ---- visibility options ----------------------------------------------
  visibilityInternal: { th: "ภายในเท่านั้น (Internal)", en: "Internal only" },
  visibilityExternal: { th: "ภายนอกเท่านั้น (External)", en: "External only" },
  visibilityPublic: { th: "สาธารณะ (Public)", en: "Public" },

  // ---- rich text editor gallery pickers --------------------------------
  photoGallery: { th: "แกลอรี่ภาพ", en: "Image Gallery" },
  videoGallery: { th: "แกลอรี่วิดีโอ", en: "Video Gallery" },
  documentGallery: { th: "แกลอรี่เอกสาร", en: "Document Gallery" },

  // ---- NMU-538: rich-text image handling --------------------------------
  imageUploadFailed: {
    th: "อัปโหลดรูปภาพไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
    en: "Image upload failed. Please try again.",
  },
  imagePasteBlockedTitle: {
    th: "นำรูปภาพที่ฝังมาออกแล้ว",
    en: "Embedded images removed",
  },
  imagePasteBlockedBody: {
    th: "เนื้อหาที่วางมีรูปภาพฝังอยู่ในตัวข้อความ ระบบนำออกให้แล้ว "
      + "กรุณาเพิ่มรูปด้วยปุ่มรูปภาพหรือแกลอรี่ เพื่อให้รูปถูกเก็บเป็นไฟล์จริง",
    en: "The pasted content had images embedded in the text; they have been "
      + "removed. Add them with the image button or the gallery so they are "
      + "stored as real files.",
  },

  // ---- news / activity --------------------------------------------------
  newsTitle: { th: "หัวข้อข่าว", en: "News Title" },
  description: { th: "คำอธิบาย", en: "Description" },
  newsContent: { th: "เนื้อหาข่าว", en: "News Content" },
  newsType: { th: "ประเภทข่าว", en: "News Type" },
  publishDate: { th: "วันที่เผยแพร่", en: "Publish Date" },
  activityTitle: { th: "ชื่อกิจกรรม", en: "Activity Title" },
  activityContent: { th: "เนื้อหากิจกรรม", en: "Activity Content" },
  activityVenue: { th: "สถานที่จัดกิจกรรม", en: "Event Location" },
  activityType: { th: "ประเภทกิจกรรม", en: "Activity Type" },
  selectActivityType: { th: "เลือกประเภทกิจกรรม", en: "Select an activity type" },
  activityDate: { th: "วันที่จัดกิจกรรม", en: "Activity Date" },
  // NMU-463: the calendar's clock badge / "เวลาปฏิบัติการ" row had no source field.
  activityTime: { th: "เวลาปฏิบัติการ", en: "Operating Time" },
  activityStartTime: { th: "เวลาเริ่มต้น", en: "Start Time" },
  activityEndTime: { th: "เวลาสิ้นสุด", en: "End Time" },
  activityAllDay: { th: "ตลอดวัน", en: "All day" },
  userGroup: { th: "กลุ่มผู้ใช้งาน", en: "User Group" },

  // ---- service link / footer link / highlight --------------------------
  /*
    NMU-575: the short name printed under the icon on the service card — a
    couple of words, not the headline sentence that ชื่อลิงค์บริการ holds.
  */
  iconNameTH: { th: "ชื่อไอคอน (ไทย)", en: "Icon Name (TH)" },
  iconNameEN: { th: "Icon Name (EN)", en: "Icon Name (EN)" },
  serviceLinkName: { th: "ชื่อลิงค์บริการ", en: "Service Link Name" },
  descriptionTH: { th: "คำอธิบาย (ไทย)", en: "Description (TH)" },
  descriptionEN: { th: "Description (EN)", en: "Description (EN)" },
  badgeTH: { th: "ป้ายกำกับ (ไทย)", en: "Tag / Label (TH)" },
  badgeEN: { th: "Tag / Label (EN)", en: "Tag / Label (EN)" },
  footerLinkName: { th: "ชื่อลิงก์", en: "Link Name" },
  linkTitle: { th: "ชื่อลิงค์", en: "Link Title" },
  itemNo: { th: "รายการที่", en: "Item" },
  highlightName: { th: "ชื่อไฮไลท์", en: "Highlight Name" },
  layer: { th: "เลเยอร์ (Layer)", en: "Layer" },
  sharedSettings: {
    th: "ตั้งค่าทั่วไป (ใช้ร่วมกันทั้งสองภาษา)",
    en: "General settings (shared by both languages)",
  },
  programTitleTH: { th: "ชื่อหลักสูตร (ไทย)", en: "Programme Title (TH)" },
  shortDescriptionTH: { th: "คำอธิบายโดยย่อ (ไทย)", en: "Short Description (TH)" },
  programContentTH: { th: "เนื้อหาหลักสูตร (ไทย)", en: "Programme Content (TH)" },
  facultyNameTH: { th: "ชื่อคณะ (ไทย)", en: "Faculty Name (TH)" },
  durationTH: { th: "ระยะเวลาศึกษา (ไทย)", en: "Duration (TH)" },
  careersTH: { th: "อาชีพหลังสำเร็จการศึกษา (ไทย)", en: "Career Paths (TH)" },
  degreeLevel: { th: "ระดับการศึกษา", en: "Degree Level" },
  selectDegreeLevel: { th: "เลือกระดับการศึกษา", en: "Select a degree level" },
  facultyCode: { th: "ต้นสังกัดวิชาการ / รหัสคณะ", en: "Academic Department / Faculty Code" },
  totalCredits: { th: "หน่วยกิตรวม", en: "Total Credits" },
  sharedProgramInfo: {
    th: "ข้อมูลหลักสูตร (ใช้ร่วมกันทั้งสองภาษา)",
    en: "Programme details (shared by both languages)",
  },

  // ---- validation / notifications --------------------------------------
  /*
    NMU-532: names the field. `${label}` is antd's own template, filled in from
    each Form.Item's label — so a bilingual dialog says "กรุณาระบุหัวข้อข่าว" on
    the Thai tab and "Please enter News Title" on the English one, and both keep
    naming the field when someone renames a label.
  */
  // eslint-disable-next-line no-template-curly-in-string
  required: { th: "กรุณาระบุ${label}", en: "Please enter ${label}" },
  successTitle: { th: "สำเร็จ", en: "Success" },
  saveSuccess: { th: "บันทึกสำเร็จ", en: "Saved successfully" },
  warningTitle: { th: "แจ้งเตือน", en: "Notice" },
  incompleteForm: {
    th: "โปรดระบุข้อมูลให้ครบถ้วน",
    en: "Please complete all required fields",
  },
};

/**
 * Returns a translator bound to a language.
 *
 * `t('save')` → "Save" in English, "บันทึก" in Thai. Passing a string that is
 * not a key returns it unchanged, so `t(someThaiLabel)` is safe.
 */
/**
 * The recommended-image-size hint, which differs per form. Kept as a builder
 * rather than one key per size so a new form does not need a new dictionary entry.
 */
export const imageSizeHint = (lang, size) =>
  lang === FORM_LANG.EN
    ? `* Recommended image size is ${size}.`
    : `* ขนาดรูปภาพที่แนะนำคือ ${size}.`;

export const formText = (lang) => (key) => {
  const entry = FORM_TEXT[key];
  if (!entry) return key;
  return lang === FORM_LANG.EN ? entry.en : entry.th;
};
